import { useState } from 'react';
import { Link } from 'react-router';
import { useLazyCommand } from '@soap/modules';
import { ChevronDown, ChevronRight, Mail } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';

const TODO_TYPE_NOTIFICATION = 'notification';

const TODO_ACTION_LINKS: Record<
  string,
  { path: string; label: string; dismissLabel?: string }
> = {
  SetupLearningEnvironment: { path: '/app/profiles/environment', label: 'Go to Environment' },
  SetupChildProfiles: { path: '/app/profiles/students', label: 'Go to Students' },
  ConfigureExternalResources: {
    path: '/app/settings/external-resources',
    label: 'Configure External Resources',
    dismissLabel: 'Not right now',
  },
  AddCurriculumTopics: { path: '/app/lessons/add-topics', label: 'Open Add Topics' },
  CreateFirstUnit: { path: '/app/lessons', label: 'Go to Plan' },
  ReviewFirstUnit: { path: '/app/lessons/review', label: 'Open Plan' },
  CompleteFirstUnit: { path: '/app/lessons/inprogress', label: 'Open Plan' },
};

export type TodoDto = {
  e302_Key?: string;
  e302_Description?: string;
  e302_Details?: string;
  e302_TodoTypeKey?: string;
};

interface TodoPanelProps {
  todos: TodoDto[];
  onClose?: () => void;
}

export function TodoPanel({ todos, onClose }: TodoPanelProps) {
  const [notificationDialogTodo, setNotificationDialogTodo] = useState<TodoDto | null>(null);
  const [completingTodoKey, setCompletingTodoKey] = useState<string | null>(null);
  const [expandedTodoKey, setExpandedTodoKey] = useState<string | null>(null);

  const [completeTodo] = useLazyCommand('C305v1_CompleteTodo');

  const handleCompleteTodo = async (todoKey: string, onSuccess?: () => void) => {
    if (completingTodoKey) return;
    setCompletingTodoKey(todoKey);
    try {
      await completeTodo({ C305_TodoKey: todoKey });
      onSuccess?.();
    } finally {
      setCompletingTodoKey(null);
    }
  };

  const handleMarkAsRead = () => {
    if (!notificationDialogTodo?.e302_Key) return;
    void handleCompleteTodo(notificationDialogTodo.e302_Key, () => {
      setNotificationDialogTodo(null);
    });
  };

  const handleDismissTodo = (todoKey: string) => {
    void handleCompleteTodo(todoKey);
  };

  if (todos.length > 0) {
    return (
      <div data-tour="help-todos" className="flex flex-col min-h-0">
        <ul className="flex-1 overflow-auto space-y-2">
          {todos.map((t) => {
            const isNotification = t.e302_TodoTypeKey === TODO_TYPE_NOTIFICATION;
            const hasDetails = !!t.e302_Details;
            const isNotificationClickable = isNotification && hasDetails;
            const isActionWithDetails = !isNotification && hasDetails;
            const actionLink = t.e302_Key ? TODO_ACTION_LINKS[t.e302_Key] : undefined;
            const isExpanded = expandedTodoKey === t.e302_Key;

            const handleHeaderClick = () => {
              if (isNotificationClickable) {
                setNotificationDialogTodo(t);
              } else if (isActionWithDetails) {
                setExpandedTodoKey(isExpanded ? null : t.e302_Key ?? null);
              }
            };

            return (
              <li key={t.e302_Key} className="rounded-lg bg-gray-50 overflow-hidden">
                <div
                  className={`flex items-center gap-2 px-3 py-2 ${
                    isNotificationClickable || isActionWithDetails
                      ? 'cursor-pointer hover:bg-gray-100'
                      : ''
                  }`}
                  onClick={
                    isNotificationClickable || isActionWithDetails ? handleHeaderClick : undefined
                  }
                  onKeyDown={
                    isNotificationClickable || isActionWithDetails
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleHeaderClick();
                          }
                        }
                      : undefined
                  }
                  role={
                    isNotificationClickable || isActionWithDetails ? 'button' : undefined
                  }
                  tabIndex={
                    isNotificationClickable || isActionWithDetails ? 0 : undefined
                  }
                >
                  {isNotification ? (
                    <Mail className="h-4 w-4 flex-shrink-0 text-gray-600" />
                  ) : (
                    <span className="h-2 w-2 rounded-full bg-orange-500 flex-shrink-0" />
                  )}
                  {isActionWithDetails && (
                    <span className="flex-shrink-0 text-gray-500">
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </span>
                  )}
                  <span className="flex-1 min-w-0">
                    {t.e302_Description ?? t.e302_Key}
                  </span>
                </div>
                {isActionWithDetails && isExpanded && (
                  <div className="border-t border-gray-100 px-3 py-2 space-y-2">
                    <p className="text-sm text-gray-700">{t.e302_Details}</p>
                    {actionLink && (
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                        <Link
                          to={actionLink.path}
                          onClick={onClose}
                          className="text-sm font-medium text-[#1a5172] hover:underline"
                        >
                          {actionLink.label}
                        </Link>
                        {actionLink.dismissLabel && t.e302_Key && (
                          <>
                            <span className="text-sm text-gray-400" aria-hidden>
                              |
                            </span>
                            <button
                              type="button"
                              onClick={() => handleDismissTodo(t.e302_Key!)}
                              disabled={completingTodoKey === t.e302_Key}
                              className="text-sm font-medium text-gray-600 hover:underline disabled:opacity-50"
                            >
                              {completingTodoKey === t.e302_Key
                                ? 'Removing...'
                                : actionLink.dismissLabel}
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>

        <Dialog
          open={!!notificationDialogTodo}
          onOpenChange={(open) => !open && setNotificationDialogTodo(null)}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {notificationDialogTodo?.e302_Description ??
                  notificationDialogTodo?.e302_Key}
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-gray-700 whitespace-pre-wrap">
                {notificationDialogTodo?.e302_Details ?? 'No details available.'}
              </p>
            </div>
            <DialogFooter>
              <Button
                onClick={handleMarkAsRead}
                disabled={!!completingTodoKey}
                className="bg-[#1a5172] hover:bg-[#164a65]"
              >
                {completingTodoKey ? 'Marking...' : "I've Read It"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div data-tour="help-todos" className="flex flex-col">
      <p className="text-sm text-gray-500 mt-1">You're all set! No tasks right now.</p>
    </div>
  );
}
